/**
 * China region detection utility.
 * Detects if the user is likely in China to avoid loading Google APIs
 * that are blocked by the Great Firewall.
 */

const CHINA_TIMEZONES = [
  'Asia/Shanghai',
  'Asia/Chongqing',
  'Asia/Harbin',
  'Asia/Urumqi',
  'Asia/Kashgar',
];

const STORAGE_KEY = '_region_china';
const STORAGE_TIMESTAMP_KEY = '_region_china_ts';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Check if a cached result exists and is still valid.
 * Returns { valid: boolean, value: boolean }
 */
function getCachedResult() {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    const timestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
    if (cached !== null && timestamp !== null) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_DURATION_MS) {
        return { valid: true, value: cached === '1' };
      }
    }
  } catch (e) {
    // localStorage not available
  }
  return { valid: false, value: false };
}

/**
 * Cache the detection result in localStorage with a timestamp.
 */
function cacheResult(inChina) {
  try {
    localStorage.setItem(STORAGE_KEY, inChina ? '1' : '0');
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, String(Date.now()));
  } catch (e) {
    // localStorage not available
  }
}

/**
 * Check timezone for China zones.
 */
function isChineseTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return CHINA_TIMEZONES.indexOf(tz) !== -1;
  } catch (e) {
    return false;
  }
}

/**
 * Check navigator.language for zh-CN hint.
 */
function isChineseLocale() {
  try {
    const lang = (navigator.language || '').toLowerCase();
    return lang === 'zh-cn' || lang === 'zh';
  } catch (e) {
    return false;
  }
}

/**
 * Try to load a small Google resource with a timeout.
 * If it fails or times out, the user is likely in China.
 * Returns Promise<boolean> - true if Google is blocked.
 */
function isGoogleBlocked(timeoutMs = 2000) {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;

    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(true); // timed out => likely blocked
      }
    }, timeoutMs);

    img.onload = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        resolve(false); // loaded => not blocked
      }
    };

    img.onerror = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        resolve(true); // error => likely blocked
      }
    };

    // Use timezone-based detection only - no external network requests needed
    // This avoids depending on any third-party CDN that may be blocked
    const tzResult = isChineseTimezone() || isChineseLocale();
    settled = true;
    clearTimeout(timer);
    resolve(tzResult);
  });
}

/**
 * Async detection: returns Promise<boolean>.
 * Uses cached result if available, otherwise detects and caches.
 */
export async function isInChina() {
  // Check cache first
  const cached = getCachedResult();
  if (cached.valid) {
    return cached.value;
  }

  // Quick timezone check
  const tzChina = isChineseTimezone();
  const localeChina = isChineseLocale();

  // If BOTH timezone AND locale suggest China, assume it's China without connectivity test
  // (to avoid dependency on potentially-blocked external services for detection)
  if (tzChina && localeChina) {
    cacheResult(true);
    return true;
  }

  // If neither timezone nor locale suggest China, allow Google services
  if (!tzChina && !localeChina) {
    cacheResult(false);
    return false;
  }

  // If only one indicator suggests China (not both), don't block Google
  // to avoid false positives from network timeouts
  cacheResult(false);
  return false;
}

/**
 * Synchronous detection: returns the cached boolean value.
 * Falls back to timezone-based detection if no cache exists.
 * This is useful for initial render decisions.
 */
export function isInChinaSync() {
  const cached = getCachedResult();
  if (cached.valid) {
    return cached.value;
  }

  // Fallback: check localStorage value set by index.html script
  try {
    const htmlDetect = localStorage.getItem(STORAGE_KEY);
    if (htmlDetect !== null) {
      return htmlDetect === '1';
    }
  } catch (e) {
    // ignore
  }

  // Only return true if BOTH timezone AND locale suggest China
  // This prevents false positives from VPNs or network issues
  const tzChina = isChineseTimezone();
  const localeChina = isChineseLocale();
  return tzChina && localeChina;
}
